import React, { useEffect, useRef, useState } from "react";
import { Search, X } from "lucide-react";

// Patient name + MRN, always on the main toolbar of Claims Queue and Encounter Review.
// DO NOT REMOVE: physicians and staff use these every day (see /app/memory/PRD.md "Permanent UI").
// Nothing fires until the API's minimum (3 characters, enforced by notes/search) or the box is emptied.
const MIN = 3;
const valid = (k, v) => { const t = v.trim(); if (!t) return true; return k === "mrn" ? t.length >= MIN : t.split(/\s+/).every((w) => w.length >= MIN); };

function Field({ k, label, ph, value, onCommit, testid, width }) {
  const [v, setV] = useState(value || "");
  const last = useRef(value || ""); // the value the page is currently searching on (trimmed)
  useEffect(() => { if ((value || "") !== last.current) { last.current = value || ""; setV(value || ""); } }, [value]);
  const commit = (raw) => { const t = raw.trim(); if (t === last.current || !valid(k, raw)) return; last.current = t; onCommit(k, t); };
  useEffect(() => {
    if (v.trim() === last.current || !valid(k, v)) return undefined;
    const t = setTimeout(() => commit(v), 500);
    return () => clearTimeout(t);
  }, [v]); // eslint-disable-line react-hooks/exhaustive-deps
  const pending = v.trim() !== last.current; // typed but not (yet) applied
  return (
    <label className="inline-flex items-center gap-1.5 rounded-[9px] px-2.5 h-[36px] bg-white transition-colors" style={{ border: `1px solid ${v.trim() && valid(k, v) ? "#141412" : "#E5E5E8"}` }} data-testid={`${testid}-${k}-box`}>
      <Search size={13} style={{ color: "#9B9B97" }} aria-hidden="true" />
      <input value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => { if (e.key === "Escape") setV(""); if (e.key === "Enter") commit(v); }}
        onBlur={() => { if (!valid(k, v)) setV(last.current); }}
        placeholder={ph} aria-label={label} className="text-[13px] outline-none bg-transparent" style={{ width, color: pending && !valid(k, v) ? "#9B9B97" : "#141412" }} data-testid={`${testid}-${k}`} />
      <button type="button" onClick={() => setV("")} aria-label={`Clear ${label}`} data-testid={`${testid}-${k}-clear`} style={{ visibility: v ? "visible" : "hidden" }}><X size={13} style={{ color: "#9B9B97" }} /></button>
    </label>
  );
}

// Thin indeterminate bar shown over the table while results load; rows stay in place underneath.
export function SearchProgress({ active, testid }) {
  return (
    <div aria-hidden={!active} role="progressbar" aria-label="Loading results" data-testid={testid} data-active={active ? "1" : "0"}
      style={{ height: 3, position: "relative", overflow: "hidden", background: active ? "#ECEAF8" : "transparent", transition: "background-color .2s" }}>
      {active ? <span className="pms-bar" /> : null}
      <style>{`.pms-bar{position:absolute;top:0;bottom:0;left:0;width:34%;background:#5B3FB8;border-radius:3px;animation:pmsMove 1.1s cubic-bezier(.32,.72,0,1) infinite}
@keyframes pmsMove{0%{transform:translateX(-100%)}100%{transform:translateX(300%)}}
@media (prefers-reduced-motion:reduce){.pms-bar{animation:none;width:100%;opacity:.5}}`}</style>
    </div>
  );
}

export function PatientMrnSearch({ patient, mrn, onCommit, testid }) {
  return (
    <div className="inline-flex items-center gap-2" data-testid={testid}>
      <Field k="patient" label="Patient name" ph="Patient name" value={patient} onCommit={onCommit} testid={testid} width={150} />
      <Field k="mrn" label="MRN" ph="MRN" value={mrn} onCommit={onCommit} testid={testid} width={96} />
    </div>
  );
}
